import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import reactStringReplace from 'react-string-replace';
import { CSSTransition } from 'react-transition-group';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import jobContainerClasses from 'components/jobs/container/index.module.css';
import { useTracking } from 'hooks';
import { buildDlpUrl } from 'utils';
import { TRACKING, USER_ACTIVITY_EVENTS } from 'utils/constant';

import {
  dispatchCurrentPageCategory,
  dispatchJobSeen,
  dispatchToggleDismiss,
  dispatchToggleSave,
} from '../../../store/jobs/actions';
import { dispatchOpenDLPModal } from '../../../store/modals/actions';
import { EVENTS } from '../../../utils';
import { logEventGTM } from '../../../utils/analyticsGTM';
import CompanyLogo from '../../companyLogo';
import { HeartIcon } from '../../svg';

import jobCardClasses from './index.module.css';

const propTypes = {
  dispatchToggleSave: PropTypes.func.isRequired,
  dispatchToggleDismiss: PropTypes.func.isRequired,
  job: PropTypes.shape({
    listingHash: PropTypes.string,
  }).isRequired,
  isSaved: PropTypes.bool.isRequired,
  isDismissed: PropTypes.bool.isRequired,
  isSeen: PropTypes.bool.isRequired,
  containerTitle: PropTypes.string.isRequired,
  dispatchOpenDLPModal: PropTypes.func.isRequired,
  enableDLPModalPopup: PropTypes.bool.isRequired,
  dispatchJobSeen: PropTypes.func.isRequired,
  isH2: PropTypes.bool,
  isRemote: PropTypes.bool,
  isApplied: PropTypes.bool,
  ignoreDismissed: PropTypes.bool,
  disableDismiss: PropTypes.bool,
  jobsPage: PropTypes.bool,
  jobSearched: PropTypes.string,
  dlpError: PropTypes.string,
  dlpJobListingHash: PropTypes.string,
  isDesktop: PropTypes.bool,
  logJobCardClickEvent: PropTypes.func,
  companySkills: PropTypes.bool,
  allJobs: PropTypes.array,
};

const JobCard = (props) => {
  const {
    jobs,
    apiParams,
    disableDismiss,
    dlpError,
    job,
    dlpJobListingHash,
    dispatchOpenDLPModal,
    isRemote,
    dispatchToggleSave,
    containerTitle,
    dispatchJobSeen,
    jobSearched,
    isH2,
    enableDLPModalPopup,
    isSaved,
    isSeen,
    isDismissed,
    currentPageCategory,
    isApplied,
    dispatchCurrentPageCategory,
    titleTag,
    logJobCardClickEvent,
    jobsPage,
    enablePaidJobTest,
    companySkills,
    dispatchToggleDismiss,
  } = props;

  const tracking = useTracking({ jobs, apiParams });

  const {
    cpc,
    companyLogo,
    companyName,
    listingHash,
    estimatedSalary,
    showNewJobBedge,
    easyApplyFlag,
  } = job;

  const [active, setActive] = useState(false);

  const TitleTag = titleTag ? titleTag : jobsPage || isH2 ? 'h2' : 'div';

  const displayCardTitle = useMemo(
    () =>
      jobsPage && jobSearched
        ? reactStringReplace(job.jobTitle, jobSearched, (match) => <b>{match}</b>)
        : job.jobTitle,
    [job, jobsPage, jobSearched]
  );

  const keywords = useMemo(() => {
    if (job?.snippets?.length) {
      return job.snippets
        .filter((snippet) => snippet)
        .join(' ')
        .trim();
    } else {
      return job.jobDescription || '';
    }
  }, [job]);

  const companyInfo = useMemo(() => {
    return (
      job.companyInfo || {
        companyLogo: job.companyLogo,
        companyName: job.companyName,
        companyInitial: job.companyInitial,
      }
    );
  }, [job]);

  const shortDesc = useMemo(
    () =>
      job.snippets?.length ? job.snippets.join('').length > 80 : job.jobDescription?.length > 120,
    [job]
  );

  const jobLocation = useMemo(
    () =>
      job?.OBJjobTags?.indexOf('Remote') !== -1 && job?.location
        ? `Remote or ${job?.location}`
        : job?.OBJjobTags?.indexOf('Remote') !== -1
        ? 'Remote'
        : job?.location || '',
    [job]
  );

  useEffect(() => {
    if (dlpError !== null) {
      if (dlpJobListingHash === job.listingHash) {
        toggleDismiss(true);
      }
    }
  }, [dlpError, job, dlpJobListingHash]);

  const toggleClass = (flag) => {
    setActive(flag);
  };

  const onJobCardClick = (event) => {
    const { metaKey, ctrlKey } = event;
    const openModal = enableDLPModalPopup && !metaKey && !ctrlKey;
    if (!currentPageCategory) dispatchCurrentPageCategory(window.pageCategory);
    dispatchJobSeen({ job });
    const jobWithDLP = {
      ...job,
      jobDLPUrl: buildDlpUrl(job),
    };
    if (job.cpc > 0 && enablePaidJobTest && !(job.source === 'ZD' && easyApplyFlag)) {
      tracking.openJobExternal(job, {
        activityType: TRACKING.LOG_ACTIVITY.DLP,
        activityData: {
          type: USER_ACTIVITY_EVENTS.JOB_OPENING.JOB_OPENING_JOB_CLICKED,
          payload: {
            url: window.location.href,
            pageCategory: window.pageCategory,
          },
        },
      });
    } else {
      if (jobWithDLP.jobDLPUrl) {
        if (openModal) {
          openDLPModal(jobWithDLP);
          tracking.logActivity(
            {
              job,
              type: USER_ACTIVITY_EVENTS.JOB_OPENING.JOB_OPENING_VIEWED_DLP,
              url: window.location.href,
              pageCategory: window.pageCategory,
            },
            TRACKING.LOG_ACTIVITY.DLP
          );
        } else {
          window.open(jobWithDLP.jobDLPUrl, '_blank');
          logJobCardClickEvent();
        }
      }
    }
  };

  const openDLPModal = (job) => {
    if (job.OBJjobTags.includes('Remote') && isRemote) {
      job.isRemote = true;
    }
    dispatchOpenDLPModal(job, window.pageCategory);
  };

  const toggleSave = () => {
    toggleClass(false);
    dispatchToggleSave({ job, containerTitle });
    logEventGTM({ event: EVENTS.jobCardSaveClick });
  };

  const toggleDismiss = (isExpired = false) => {
    job.isExpired = isExpired;
    dispatchToggleDismiss({ job, containerTitle });
    logEventGTM({ event: EVENTS.jobCardDismissClick });
  };

  const showLastActions = () => {
    const { actionDateSince, postedDate, hrefOrigin, isSponsored } = job;
    if (hrefOrigin && (hrefOrigin === '/matches/saved/' || hrefOrigin === '/matches/applied/')) {
      if (isApplied) {
        return `Applied ${actionDateSince}`;
      }

      if (isSaved) {
        return `Saved ${actionDateSince}`;
      }
    }
    if (isSponsored) {
      return 'Sponsored';
    }
    if (isSeen) {
      return 'Viewed';
    }

    return postedDate;
  };

  return (
    <CSSTransition in={!isDismissed} timeout={200} classNames="bucketListItemFade" unmountOnExit>
      <div
        data-hash={listingHash}
        data-dlp={job.cpc > 0 ? 'bypass' : 'view'}
        className={classnames(
          jobCardClasses.jobCardBucketListItem,
          jobContainerClasses.bucketListItem,
          'bucketListItem jobCard new-design'
        )}
        onClick={onJobCardClick}
      >
        {easyApplyFlag && <div className={jobContainerClasses.owEasyApplyLabel}>Easy Apply</div>}

        <div className="bucketListItemLogo">
          <CompanyLogo
            size="xsmall"
            className="CompanyLogoImg"
            companyLogo={companyLogo || companyInfo.companyLogo}
            companyName={companyName}
            imgWidth={40}
            imgHeight={40}
          />
        </div>

        {/* save dislike Icons */}
        <div
          className={classnames(jobCardClasses.bucketListItemSave, jobCardClasses['new-design'], {
            'icon-cardlike-filled': isSaved,
            'icon-cardlike': !isSaved,
            'like-touched': active,
          })}
        >
          {!disableDismiss && (
            <div
              className="jobCardtIcon icon-prohibited"
              onClick={(e) => {
                e.stopPropagation();
                toggleDismiss();
              }}
            />
          )}
          <div
            className="icon-saved"
            onClick={(e) => {
              e.stopPropagation();
              toggleSave();
            }}
            onMouseDown={() => {
              toggleClass(true);
            }}
          >
            <HeartIcon className={classnames({ isSaved })} />
          </div>
        </div>
        <div
          className={classnames(
            jobCardClasses.bucketListItemContent,
            `bucketListItemContent ${isSeen ? 'seen' : 'unseen'}JobCard`
          )}
        >
          <div className="bucketListItemContentTop">
            <div className="owlCompanyName">{companyName || 'Company Unknown'}</div>
            <div className={classnames('owlCompanyLocation new-design')}>{jobLocation}</div>
            <TitleTag className={classnames('owlJobTitle new-design')}>{displayCardTitle}</TitleTag>
          </div>

          {!!keywords && (
            <div
              className={classnames('owlJobDescriptionFull new-design', { short: shortDesc })}
              dangerouslySetInnerHTML={{
                __html: keywords,
              }}
            />
          )}

          {companySkills && !keywords && !!(job.skillsets && job.skillsets.length) && (
            <div className="owlJobDescription new-design">
              {'Skills: ' + (job?.skillsets?.slice(0, 8)?.join(', ') ?? '')}
            </div>
          )}

          <div
            className={classnames(
              jobCardClasses.bucketListItemContentBottom,
              jobCardClasses['new-design'],
              {
                'sponsored-job': cpc > 0 && showNewJobBedge,
              }
            )}
          >
            <p className="owlEstimatedTimeJob d-flex align-items-center">
              {showLastActions()}
              {showNewJobBedge && <span className="owlNewLabel">new</span>}
            </p>
            <p className="owlSalaryJob">{estimatedSalary}</p>
          </div>
        </div>
      </div>
    </CSSTransition>
  );
};

const mapDispatchToProps = {
  dispatchToggleDismiss,
  dispatchToggleSave,
  dispatchOpenDLPModal,
  dispatchJobSeen,
  dispatchCurrentPageCategory,
};

const mapStateToProps = ({ jobs, locals = {}, modals = {} }, ownProps) => {
  const {
    job: { listingHash },
    ignoreDismissed,
  } = ownProps;
  const {
    favoriteJobs = [],
    dismissedJobs = [],
    viewedJobs = [],
    currentPageCategory,
    appliedJobs = [],
  } = jobs;
  const dlpPopUpOpen = modals.dlp && modals.dlp.open;
  return {
    dlpPopUpOpen,
    enableDLPModalPopup: !!locals.enableDLPModalPopup,
    enablePaidJobTest: locals.enablePaidJobTest,
    isSaved: favoriteJobs.indexOf(listingHash) > -1,
    isApplied: appliedJobs.indexOf(listingHash) > -1,
    isDismissed: !ignoreDismissed && dismissedJobs.indexOf(listingHash) > -1,
    isSeen: viewedJobs.indexOf(listingHash) > -1,
    currentPageCategory,
    dlpError: modals.dlp.error,
    dlpJobListingHash: modals.dlp.dlpDetail?.listingHash,
  };
};

JobCard.propTypes = propTypes;

export default connect(mapStateToProps, mapDispatchToProps)(JobCard);
